import { useQuery, useQueryClient } from '@tanstack/react-query';
import { daysRemainingFromUtcDate, isDateInPast } from '@utils/date';
import { useCustomerApi } from 'services/CompanyService/CompanyService';

interface QueryOptions {
  enabled?: boolean;
}

export const OWN_COMPANY_QUERY_KEY = 'own-company';
/**
 * IN_TRIAL: Customer is in free trial, this trial may or may not be expired
 * TRIAL_EXPIRED: Customer is in free trial and the trial has expired
 * NOT_IN_TRIAL: Customer is not in free trial
 */
type FreeTrialStatus = 'IN_TRIAL' | 'TRIAL_EXPIRED' | 'NOT_IN_TRIAL';

/**
 * The number of days remaining in the free trial
 * This variable can be negative and 0 if the trial is expired
 * This variable can be undefined if the customer is not in free trial, hence undefined is very much intentional
 */
type FreeTrialDaysRemaining = number | undefined;

function useOwnCompany(options?: QueryOptions) {
  const customerApi = useCustomerApi();
  return useQuery({
    queryKey: [OWN_COMPANY_QUERY_KEY],
    queryFn: () => customerApi.getCurrentCustomer(),
    select: (data) => {
      const customer = data.data;
      /**
       * If customer is free trial, calculate the days remaining else set it to undefined
       */
      const freeTrialDaysRemaining: FreeTrialDaysRemaining = customer.trialEndDate
        ? daysRemainingFromUtcDate(customer.trialEndDate)
        : undefined;
      // If customer is free trial, check if it is expired
      let freeTrialStatus: FreeTrialStatus = customer.trialEndDate ? 'IN_TRIAL' : 'NOT_IN_TRIAL';
      const isTrialEnded = customer.trialEndDate ? isDateInPast(customer.trialEndDate) : undefined;
      if (freeTrialStatus === 'IN_TRIAL' && isTrialEnded !== undefined && isTrialEnded) {
        freeTrialStatus = 'TRIAL_EXPIRED';
      }
      const hasRemainingTrialDays =
        freeTrialStatus === 'IN_TRIAL' && freeTrialDaysRemaining !== undefined && freeTrialDaysRemaining >= 0;

      return { ...customer, freeTrialDaysRemaining, freeTrialStatus, hasRemainingTrialDays };
    },
    enabled: options?.enabled,
    ...options,
  });
}

export const useInvalidateOwnCompanyQuery = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [OWN_COMPANY_QUERY_KEY] });
  };
};

export type FrontendCustomer = NonNullable<ReturnType<typeof useOwnCompany>['data']>;

export default useOwnCompany;
